<template>
    <BaseCard padding="lg" class="flex flex-col gap-6">
        <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <h2 class="display-small-serif text-ink">{{ t("teamPage.youAndTeam") }}</h2>
            <p class="details text-ink-muted">{{ t("teamPage.coverage", { count: report.members.length, results: resultCount, profiles: profileCount }) }}</p>
        </div>
        <div class="flex flex-wrap items-center justify-center gap-2 rounded-lgr bg-surface px-4 py-10">
            <BaseTooltip v-for="member in report.members" :key="member.id" :trigger-aria-label="memberLabel(member)" :as-child="false" open-on-click content-class="fs-mask">
                <template #trigger>
                    <PerspectiveAvatar :name="member.name" :letters="member.result?.letters" aria-hidden="true" />
                </template>
                <template #content>{{ memberLabel(member) }}</template>
            </BaseTooltip>
        </div>
        <div class="team-overview-row">
            <h3 class="body-small-semibold flex items-start gap-2 text-ink"><i class="bi bi-diagram-3" aria-hidden="true" />{{ t("teamPage.makeup") }}</h3>
            <div class="flex flex-col gap-4">
                <p class="body text-ink">{{ t("teamPage.makeupIntro", { count: resultCount, total: report.members.length }) }}</p>
                <ul v-if="report.dimensions.length" class="body list-disc space-y-2 pl-5 text-ink-muted">
                    <li v-for="dimension in dimensions" :key="dimension.id">
                        {{
                            t("teamPage.dimensionSummary", {
                                dimension: dimension.title,
                                summary:
                                    dimension.spread === "balanced"
                                        ? t("teamPage.balanced")
                                        : t("teamPage.leans", { side: dimension.average < 0 ? dimension.left_label.toLowerCase() : dimension.right_label.toLowerCase() }),
                            })
                        }}
                    </li>
                </ul>
                <p v-else class="body text-ink-muted">{{ t("teamPerspective.empty") }}</p>
            </div>
        </div>
        <div class="team-overview-row">
            <h3 class="body-small-semibold flex items-start gap-2 text-ink"><i class="bi bi-person-dash" aria-hidden="true" />{{ t("teamPage.missingTitle") }}</h3>
            <p class="body text-ink-muted">{{ missingNames.length ? t("teamPage.missing", { names: missingNames.join(", ") }) : t("teamPage.allResults") }}</p>
        </div>
        <div class="team-overview-row border-t border-border pt-6">
            <h3 class="body-small-semibold flex items-start gap-2 text-ink"><i class="bi bi-lightbulb" aria-hidden="true" />{{ t("teamPage.guidance") }}</h3>
            <div class="flex flex-col gap-4">
                <p class="body text-ink-muted">{{ t("teamPage.guidanceIntro") }}</p>
                <div v-for="dimension in dimensions" :key="dimension.id" class="flex flex-col gap-2">
                    <h4 class="body-small-semibold text-ink">{{ dimension.title }}</h4>
                    <MarkdownText :text="dimension.guidance.overview" class="body text-ink-muted" />
                </div>
                <p class="details text-ink-muted">{{ t("teamPerspective.sharing") }}</p>
            </div>
        </div>
    </BaseCard>
</template>
<script setup lang="ts">
import type { TeamPerspectiveMember, TeamPerspectiveReport } from "~vue/api/generated/team-perspective";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import PerspectiveAvatar from "~vue/components/PerspectiveAvatar.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { report } = defineProps<{ report: TeamPerspectiveReport }>();
const { t } = useI18n();
const dimensions = computed(() => [...report.dimensions].sort((a, b) => ["EI", "SN", "TF", "JP"].indexOf(a.id) - ["EI", "SN", "TF", "JP"].indexOf(b.id)));
const resultCount = computed(() => report.members.filter((member) => member.result?.dimensions.length).length);
const profileCount = computed(() => report.members.filter((member) => member.profile_url).length);
const missingNames = computed(() => report.members.filter((member) => !member.result?.dimensions.length).map((member) => member.name));
function memberLabel(member: TeamPerspectiveMember) {
    const name = member.id === report.viewer_id ? t("teamPage.youName", { name: member.name }) : member.name;
    return `${name} · ${member.result?.archetype || t("teamPage.noSharedResult")}`;
}
</script>
<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .team-overview-row {
        @apply grid gap-3 md:grid-cols-[11rem_minmax(0,1fr)] md:gap-6;
    }
}
</style>
